import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Container, Row, Card, CardBody, CardHeader, Nav, NavItem, NavLink, TabContent, TabPane, Form, FormGroup, Input, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import Breadcrumbs from '../CommonElements/Breadcrumbs';
import Table from '../CustomComponent/Tables/Table';
import { TableColumn } from 'react-data-table-component';
import { Eye, Pencil, XCircle } from 'lucide-react';
import ActionDropdown from '../CustomComponent/ActionDropdown';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { Btn } from '../AbstractElements';
import { Href } from '../Utils/Constants';
import { useUser } from '../Contexts/UserContext';
import { getClients, getProperties, getPropertyUtilities, getUtility, getMeters, getUtilities, getPropertyUtilityMeters, editPropertyMeter } from '../Services/PortfolioService';
import { formatDate, sortByKey } from '../Utils';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import SelectDropdown from '../CustomComponent/Form/SelectDropdown';

const schema = yup.object().shape({
    company_id: yup.string().trim().optional(),
    property_id: yup.string().trim().optional(),

    meter_id: yup
        .string().trim()
        .required('Meter ID is required'),

    meter_name: yup
        .string().trim()
        .required('Meter Name is required'),

    meter_no_1: yup
        .string().trim()
        .required('Meter Number is required'),

    meter_no_2: yup
        .string().trim()
        .optional(),

    meter_type: yup
        .string().trim()
        .required('Meter Type is required'),

    site_id: yup
        .string().trim()
        .required('Site ID is required'),

    utility_id: yup
        .string().trim()
        .required('Utility is required'),

    supplier_id: yup
        .string().trim()
        .required('Supplier is required'),

    location: yup
        .string().trim()
        .optional(),

    status: yup
        .string().trim()
        .required('Status is required'),

    date_installed: yup
        .string().trim()
        .nullable()
        .optional(),

    contract_end_date: yup
        .string().trim()
        .nullable()
        .optional(),

    data_collector: yup
        .string().trim()
        .nullable()
        .optional(),

    meter_operator: yup
        .string().trim()
        .nullable()
        .optional(),
});

export default function MeterReport() {
    const navigate = useNavigate();
    const { type } = useUser();
    const [clients, setClients] = useState<any>([]);
    const [properties, setProperties] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [meters, setMeters] = useState<any>([]);
    const [utilities, setUtilities] = useState<any>([]);
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false)

    const [editModalOpen, setEditModalOpen] = useState(false);
    const toggleEditModal = () => setEditModalOpen(!editModalOpen);

    const { control, setValue, watch, handleSubmit, formState: { errors }, trigger, getValues, reset } = useForm({
        resolver: yupResolver(schema), // Use the schema for validation
        defaultValues: {
            meter_name: '',
            meter_no_1: '',
            meter_no_2: '',
            meter_type: '',
            site_id: '',
            utility_id: '',
            supplier_id: '',
            location: '',
            status: '',
            date_installed: '',
            contract_end_date: '',
            data_collector: '',
            meter_operator: ''
        }
    });

    const company_id = watch('company_id');
    const utilityId = watch('utility_id');
    const meter_type = watch('meter_type');

    // Fetch suppliers when utility changes
    useEffect(() => {
        if (utilityId) fetchUtility(utilityId).then(data => setSuppliers(sortByKey(data.suppliers as any[], 'name')));
    }, [utilityId]);

    const fetchClients = async () => {
        try {
            const data: any[] = await getClients();
            setClients(sortByKey(data, 'name'));
        } catch (error: any) {
            toast.error(error?.message || 'Failed to fetch clients');
        }
    };

    const fetchUtilities = async () => {
        try {
            const response = await getUtilities();
            setUtilities(response)
        } catch (error: any) {
            toast.error(error?.message || "Failed to fetch Properties");
        }
    };

    const fetchUtility = async (utility_id: string) => {
        try {
            const data = await getUtility(utility_id);
            return data;
        } catch (error) {
            console.error("Error fetching utility suppliers:", error);
        }
    };

    const fetchProperties = async (clientId: string) => {
        if (clientId == '') return
        const { properties } = await getProperties(clientId);
        setProperties(properties)
    }

    const fetchMeters = async (clientId: string) => {
        if (clientId == '') return
        const meterData = await getMeters(clientId);
        setMeters(meterData);
    }

    useEffect(() => {
        fetchClients();
        fetchUtilities();
        const storedClient = localStorage.getItem('selectedClient');
        if (storedClient) {
            setSelectedClient(storedClient)
            setValue('company_id', storedClient)
        }
    }, []);

    useEffect(() => {
        if (!selectedClient && !company_id) return;
        const clientId = (selectedClient || company_id) as string;
        fetchMeters(clientId)
        if (type === 'tenant') {
            fetchProperties(clientId)
        }
        localStorage.setItem('selectedClient', clientId)
    }, [selectedClient, company_id]);

    const handleEditMeter: SubmitHandler<any> = async (data) => {
        try {
            const meterId = data.meter_id;
            if (!meterId) return;

            const formData = new FormData();
            formData.append('meter_name', data.meter_name);
            formData.append('meter_type', data.meter_type);
            formData.append('meter_no_1', data.meter_no_1);
            formData.append('meter_no_2', data.meter_no_2);
            formData.append('meter_type', data.meter_type);
            formData.append('site_id', data.site_id);
            formData.append('utility_id', data.utility_id);
            formData.append('supplier_id', data.supplier_id);
            formData.append('location', data.location);
            // formData.append('date_installed', data.date_installed);
            // formData.append('contract_end_date', data.contract_end_date);
            if (data.date_installed) {
                formData.append('date_installed', data.date_installed);
            }
            if (data.contract_end_date) {
                formData.append('contract_end_date', data.contract_end_date);
            }

            formData.append('data_collector', data.data_collector || '');
            formData.append('meter_operator', data.meter_operator || '');
            formData.append('status', data.status);

            await editPropertyMeter(selectedClient!, data.property_id!, meterId, formData);
            toast.success("Meter updated!");
            fetchMeters(selectedClient)
            toggleEditModal();
        } catch (error: any) {
            toast.error(error.message || "Failed to update meter");
        }
    };

    const propertyOptions = properties.map((property: { property_id: string; name: string }) => ({
        value: property.property_id, // Assuming property_id is the unique identifier
        label: property.name // Assuming property_name is the display name
    }));

    const utilityOptions = utilities.map((utility: any) => ({ label: utility.name, value: utility.utility_id }));
    const supplierOptions = suppliers.map((supplier: any) => ({ label: supplier.name, value: supplier.supplier_id }));

    const meterTypeOptions = [
        { label: "MPAN", value: 'MPAN' },
        { label: "SPID", value: 'SPID' },
        { label: "MPRN", value: 'MPRN' },
        { label: "CHP", value: 'CHP' },
        { label: 'SUB METER', value: 'SUB METER' }
    ];

    const meterStatusOptions = [
        { label: 'Active', value: 'active' },
        { label: 'Old', value: 'old' },
        { label: 'New', value: 'new' },
        // {label: 'Inactive', value: 'inactive'}
    ]

    const meterTableColumns: TableColumn<any>[] = [
        {
            name: "Property",
            cell: (row) => (
                type === 'client' ? (
                    <span>{row.property.name}</span>
                ) : (
                    <span
                        className="text-primary cursor-pointer"
                        onClick={() => {
                            reset({
                                property_id: row.property_id,
                                meter_id: row.meter_id,
                                meter_name: row.name,
                                meter_type: row.meter_type,
                                meter_no_1: row.meter_no_1,
                                meter_no_2: row.meter_no_2,
                                site_id: row.site_id,
                                utility_id: row.utility_id,
                                supplier_id: row.supplier_id,
                                location: row.location,
                                status: row.status,
                                date_installed: row.date_installed,
                                contract_end_date: row.contract_end_date,
                                data_collector: row.data_collector,
                                meter_operator: row.meter_operator
                            });
                            toggleEditModal();
                        }}
                    >
                        {row.property.name}
                    </span>
                )
            ),
            width: '200px',
            sortable: false,
            center: false,
        },
        {
            name: "Meter Name",
            selector: (row) => row.name,
            width: '200px',
            sortable: false,
            center: false,
        },
        {
            name: "Type",
            selector: (row) => row.meter_type,
            width: '120px',
            sortable: true,
            center: false,
            // color: 'orange'
        },
        {
            name: "Meter No.",
            selector: (row) => (
                <div>
                    <div>{row.meter_no_1 || ''}</div>
                    <div>{row.meter_no_2 || ''}</div>
                </div>
            ),
            // selector: (row) => row.site_id,
            width: '150px',
            sortable: true,
            center: false,
            // color: 'orange'
        },
        {
            name: "Utility",
            cell: (row) => row.utility?.name || '',
            sortable: false,
            center: false,
        },
        {
            name: "Supplier",
            cell: (row) => row.supplier?.name || '',
            sortable: false,
            center: false,
        },
        {
            name: "Data Collector",
            cell: (row) => row.data_collector || '',
            width: '120px',
            sortable: false,
            center: false,
        },
        {
            name: "Meter Operator",
            cell: (row) => row.meter_operator || '',
            width: '140px',
            sortable: false,
            center: false,
        },
        {
            name: "Status",
            cell: (row) => row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : '',
            sortable: false,
            center: true,
        },
        {
            name: 'Contract End date',
            cell: (row) => row.contract_end_date ? formatDate(row.contract_end_date) : '',
            width: '140px',
            sortable: false,
            center: false,
        },
    ];

    return (
        <>
            <Row>
                <Col md="8" className=''>
                    <Breadcrumbs pageTitle='Meter Report' />
                </Col>
                {type === 'tenant' && (
                    <Col md="4" className="d-flex flex-column justify-content-end text-start mt-3">
                        <FormGroup>
                            <Col><Label>Clients</Label><span className="text-danger">*</span></Col>
                            <Controller
                                name="company_id"
                                control={control}
                                render={({ field }) => (
                                    <Input type="select" {...field}>
                                        <option selected disabled value=''>Choose...</option>
                                        {clients.map((item: { client_id: string; name: string }, i: number) => (
                                            <option key={i} value={item.client_id}>{item.name}</option>
                                        ))}
                                    </Input>
                                )}
                            />
                            {errors.company_id && <p className='text-danger' style={{ width: '100%', textAlign: "left" }}>{errors.company_id.message}</p>} {/* Error message */}
                        </FormGroup>
                    </Col>
                )}
            </Row>
            <Container fluid className='default-dashboard'>
                <Row>
                    <Col sm="12">
                        <Table title='Meter Report' tableColumns={meterTableColumns} data={meters} />
                    </Col>
                </Row>

                <Modal modalClassName="modal-bookmark" isOpen={editModalOpen} toggle={toggleEditModal} size="lg">
                    <ModalHeader toggle={toggleEditModal}>{'Update Meter'}</ModalHeader>
                    <ModalBody>
                        <Form onSubmit={handleSubmit(handleEditMeter)}>
                            <Row style={{ marginTop: '5px' }}>

                                <Col sm={4}>
                                    <FormGroup>
                                        <Controller
                                            name="property_id"
                                            control={control}
                                            render={({ field }) => (
                                                <SelectDropdown
                                                    title="Property"
                                                    options={propertyOptions}
                                                    value={field.value}
                                                    onOptionSelected={(val) => field.onChange(val)}
                                                />
                                            )}
                                        />
                                        {errors.utility_id && <p className="text-danger">{errors.utility_id.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <FormGroup>
                                        <Col><Label for="meter_name">Meter Name</Label><span className="text-danger">*</span></Col>
                                        <Controller
                                            name="meter_name"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    id="meter_name"
                                                    type="text"
                                                    placeholder="Enter Meter Name"
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.meter_name && <p className="text-danger">{errors.meter_name.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <FormGroup>
                                        <Controller
                                            name="meter_type"
                                            control={control}
                                            render={({ field }) => (
                                                <SelectDropdown
                                                    title="Meter Type"
                                                    options={meterTypeOptions}
                                                    value={field.value}
                                                    onOptionSelected={(val) => field.onChange(val)}
                                                />
                                            )}
                                        />
                                        {errors.meter_type && <p className="text-danger">{errors.meter_type.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <FormGroup>
                                        <Col><Label for="meter_no_1">Meter Number</Label><span className="text-danger">*</span></Col>
                                        <Controller
                                            name="meter_no_1"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    id="meter_no_1"
                                                    type="text"
                                                    placeholder="Enter Meter Number"
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.meter_no_1 && <p className="text-danger">{errors.meter_no_1.message}</p>}

                                        {meter_type == 'MPAN' &&
                                            <>
                                                <Controller
                                                    name="meter_no_2"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            id="meter_no_2"
                                                            type="text"
                                                            placeholder="Enter Meter Number 2"
                                                            {...field}
                                                        />
                                                    )}
                                                />
                                                {errors.meter_no_2 && <p className="text-danger">{errors.meter_no_2.message}</p>}
                                            </>
                                        }
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <FormGroup>
                                        <Col><Label for="site_id">Site ID</Label></Col>
                                        <Controller
                                            name="site_id"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    id="site_id"
                                                    type="text"
                                                    placeholder="Enter Site ID"
                                                    {...field}
                                                />
                                            )}
                                        />
                                        {errors.site_id && <p className="text-danger">{errors.site_id.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <FormGroup>
                                        <Controller
                                            name="utility_id"
                                            control={control}
                                            render={({ field }) => (
                                                <SelectDropdown
                                                    title="Utility"
                                                    options={utilityOptions}
                                                    value={field.value}
                                                    onOptionSelected={(val) => field.onChange(val)}
                                                />
                                            )}
                                        />
                                        {errors.utility_id && <p className="text-danger">{errors.utility_id.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <FormGroup>
                                        <Controller
                                            name="supplier_id"
                                            control={control}
                                            render={({ field }) => (
                                                <SelectDropdown
                                                    title="Supplier"
                                                    options={supplierOptions}
                                                    value={field.value}
                                                    onOptionSelected={(val) => field.onChange(val)}
                                                />
                                            )}
                                        />
                                        {errors.supplier_id && <p className="text-danger">{errors.supplier_id.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <FormGroup>
                                        <Col><Label for="location">Location</Label></Col>
                                        <Controller
                                            name="location"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    id="location"
                                                    type="text"
                                                    placeholder="Enter Meter location"
                                                    {...field}
                                                    value={field.value || ''}
                                                />
                                            )}
                                        />
                                        {errors.location && <p className="text-danger">{errors.location.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <Col><Label for="data_collector">Data Collector</Label></Col>
                                    <FormGroup>
                                        <Controller
                                            name="data_collector"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    id="data_collector"
                                                    type="text"
                                                    placeholder="Enter Data Collector"
                                                    {...field}
                                                    value={field.value || ''}
                                                />
                                            )}
                                        />
                                        {errors.data_collector && <p className="text-danger">{errors.data_collector.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <Col><Label for="meter_operator">Meter Operator</Label></Col>
                                    <FormGroup>
                                        <Controller
                                            name="meter_operator"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    id="meter_operator"
                                                    type="text"
                                                    placeholder="Enter Meter Operator"
                                                    {...field}
                                                    value={field.value || ''}
                                                />
                                            )}
                                        />
                                        {errors.meter_operator && <p className="text-danger">{errors.meter_operator.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <Col><Label for="date_installed">Date Installed</Label></Col>
                                    <FormGroup>
                                        <Controller
                                            name="date_installed"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="date"
                                                    id="date_installed"
                                                    placeholder='Select the date installed'
                                                    {...field}
                                                    value={field.value || ''}
                                                />
                                            )}
                                        />
                                        {errors.date_installed && <p className="text-danger">{errors.date_installed.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <Col><Label for="contract_end_date">Contract End Date</Label></Col>
                                    <FormGroup>
                                        <Controller
                                            name="contract_end_date"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="date"
                                                    id="contract_end_date"
                                                    placeholder='Select Contract End Date'
                                                    {...field}
                                                    value={field.value || ''}
                                                />
                                            )}
                                        />
                                        {errors.contract_end_date && <p className="text-danger">{errors.contract_end_date.message}</p>}
                                    </FormGroup>
                                </Col>

                                <Col sm={4}>
                                    <FormGroup>
                                        <Controller
                                            name="status"
                                            control={control}
                                            render={({ field }) => (
                                                <SelectDropdown
                                                    title="Status"
                                                    options={meterStatusOptions}
                                                    value={field.value}
                                                    onOptionSelected={(val) => field.onChange(val)}
                                                />
                                            )}
                                        />
                                        {errors.status && <p className="text-danger">{errors.status.message}</p>}
                                    </FormGroup>
                                </Col>
                            </Row>
                            <div className="form-actions mt-3 text-end">
                                <button type="reset" className="btn btn-secondary mx-2" onClick={() => { reset(); toggleEditModal(); }}>Cancel</button>
                                <button type="submit" className="btn btn-primary mx-2" disabled={loading}>Update</button>
                            </div>
                        </Form>
                    </ModalBody>
                </Modal>
            </Container>
        </>
    )
}